import { useCallback, useEffect, useMemo, useState } from "react";
import {
  approveProcurementOrder,
  closeProcurementOrder,
  listProcurementOrders,
  markProcurementOrderOrdered,
  receiveProcurementOrder,
  receiveProcurementOrderPartial,
  rejectProcurementOrder,
  reviewProcurementOrder,
} from "../services/procurementService";
import ProcurementProcessingTable from "../components/procurement/ProcurementProcessingTable";
import procurementErrorMessage from "../components/procurement/procurementErrorMessage";

const PROCESSING_STATUSES = [
  "requested",
  "under_review",
  "approval_pending",
  "approved",
  "approved_for_ordering",
  "ordered",
  "partially_received",
];
const INITIAL_FILTERS = {
  status: "",
  type: "",
  associationType: "",
  needsLink: "",
  supplierId: "",
  supplier: "",
  clientId: "",
  projectId: "",
  workOrderId: "",
  assignedTo: "",
  createdBy: "",
  from: "",
  to: "",
  search: "",
};

export default function ProcurementProcessingPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const activeFilters = useMemo(() => {
    if (filters.status) return filters;
    return { ...filters, status: PROCESSING_STATUSES.join(",") };
  }, [filters]);

  const load = useCallback(async (nextFilters) => {
    try {
      setLoading(true);
      setError("");
      const result = await listProcurementOrders(nextFilters);
      const list = Array.isArray(result?.items) ? result.items : [];
      const scoped = nextFilters.status
        ? list
        : list.filter((item) => PROCESSING_STATUSES.includes(item.status));
      setItems(scoped);
    } catch (err) {
      setError(procurementErrorMessage(err, "Verwerkingslijst laden mislukt."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initiale load.
  useEffect(() => {
    load({ ...INITIAL_FILTERS, status: PROCESSING_STATUSES.join(",") });
  }, [load]);

  const runAction = async (item, actionKey) => {
    const id = item?._id;
    if (!id) return;
    setBusyId(id);
    setError("");
    try {
      if (actionKey === "review") await reviewProcurementOrder(id);
      if (actionKey === "approve") await approveProcurementOrder(id);
      if (actionKey === "reject") await rejectProcurementOrder(id);
      if (actionKey === "order") await markProcurementOrderOrdered(id);
      if (actionKey === "receive_partial") await receiveProcurementOrderPartial(id);
      if (actionKey === "receive") await receiveProcurementOrder(id);
      if (actionKey === "close") await closeProcurementOrder(id);
      await load(activeFilters);
    } catch (err) {
      setError(procurementErrorMessage(err, "Verwerkingsactie mislukt."));
    } finally {
      setBusyId("");
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const submitFilter = (event) => {
    event.preventDefault();
    load(activeFilters);
  };

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">Inkoopverwerking</h1>
        <p className="text-sm text-slate-600">Controller/backoffice overzicht voor beoordeling, bestelling en ontvangst.</p>
      </section>

      <form onSubmit={submitFilter} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-3">
          <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Alle verwerkingsstatussen</option>
            {PROCESSING_STATUSES.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
            <option value="received">received</option>
            <option value="closed">closed</option>
            <option value="rejected">rejected</option>
            <option value="nog_te_koppelen">nog_te_koppelen</option>
            <option value="gekoppeld">gekoppeld</option>
            <option value="besteld">besteld</option>
            <option value="opgehaald">opgehaald</option>
            <option value="ontvangen">ontvangen</option>
            <option value="gecontroleerd">gecontroleerd</option>
          </select>
          <select value={filters.type} onChange={(e) => updateFilter("type", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Alle types</option>
            <option value="material_order">material_order</option>
            <option value="service_order">service_order</option>
          </select>
          <select value={filters.associationType} onChange={(e) => updateFilter("associationType", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Alle koppelingen</option>
            <option value="workorder">Werkbon</option>
            <option value="project">Project</option>
            <option value="voorraad_magazijn">Voorraad / magazijn</option>
            <option value="nog_te_koppelen">Nog te koppelen</option>
          </select>
          <select value={filters.needsLink} onChange={(e) => updateFilter("needsLink", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Nog te koppelen: alle</option>
            <option value="true">Ja</option>
            <option value="false">Nee</option>
          </select>
          <input value={filters.search} onChange={(e) => updateFilter("search", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Zoek order/leverancier/context..." />
          <input value={filters.assignedTo} onChange={(e) => updateFilter("assignedTo", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="assignedTo (naam/e-mail)" />
          <input value={filters.createdBy} onChange={(e) => updateFilter("createdBy", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="createdBy (naam/e-mail)" />
          <input value={filters.supplier} onChange={(e) => updateFilter("supplier", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="leverancier" />
          <input value={filters.supplierId} onChange={(e) => updateFilter("supplierId", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="supplierId" />
          <input value={filters.clientId} onChange={(e) => updateFilter("clientId", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="clientId" />
          <input value={filters.projectId} onChange={(e) => updateFilter("projectId", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="projectId" />
          <input value={filters.workOrderId} onChange={(e) => updateFilter("workOrderId", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="workOrderId" />
          <input type="date" value={filters.from} onChange={(e) => updateFilter("from", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input type="date" value={filters.to} onChange={(e) => updateFilter("to", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button type="submit" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">Filter toepassen</button>
        </div>
      </form>

      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <ProcurementProcessingTable
        items={items}
        loading={loading}
        busyId={busyId}
        onAction={runAction}
      />
    </div>
  );
}
